import React, { useEffect, useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import {
  Bookmark,
  Loader2,
  Menu,
  PhoneCall,
  Star,
  StarHalf,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import LeftSidebar from '@/js/components/sidebars/LeftSidebar';
import SidebarSheetWrapper from '@/js/components/sidebars/SidebarSheetWrapper';
import Breadcrumb from '@/js/components/Breadcrumb';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import createApiClient from '@/api/axiosInstance';

const steps = [{ id: 1, label: 'Manufacturer Profile' }];

const BuyerManufacturerProfile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiClient = createApiClient(navigate, dispatch);

  const [sectionId, setSectionId] = useState(1);
  const { manufacturerId } = useParams();

  const [isBookmarked, setIsBookmarked] = useState(false);
  const [manufacturer, setManufacturer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [OpenPlant, setOpenPlant] = useState(false);
  const [OpenCertification, setOpenCertification] = useState(false);
  const [OpenVerified, setOpenVerified] = useState(false);

  const toggleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };

  useEffect(() => {
    const getManufacturerDetails = async () => {
      try {
        setLoading(true);
        const { data } = await apiClient.post(
          '/api/shortlisted-manufacturer/get/profile',
          JSON.stringify({
            manufacturer_id: manufacturerId,
          }),
        );
        setManufacturer(data?.data);
      } catch (error) {
        const error_message = error?.response?.data?.error_message;
        console.error('Error: ', error_message || error);
      } finally {
        setLoading(false);
      }
    };

    getManufacturerDetails();
  }, [manufacturerId]);

  const companyOverview = useMemo(
    () => [
      {
        heading: "Owner's Name",
        value:
          [manufacturer?.owner_first_name, manufacturer?.owner_last_name]
            .filter(Boolean)
            .join(' ') || '-',
      },
      {
        heading: 'Nature Of Business',
        value: manufacturer?.nature_of_business ?? '-',
      },
      {
        heading: 'Establishment Year',
        value: manufacturer?.establishment_year ?? '-',
      },
      {
        heading: 'No. Of Employees',
        value: manufacturer?.no_of_employee ?? '-',
      },
      {
        heading: 'Name Of Sales Person',
        value: manufacturer?.name_of_sales_person ?? '—',
      },
      {
        heading: 'Type Of Organisation',
        value: manufacturer?.type_of_organisation ?? '-',
      },
      { heading: 'Address', value: manufacturer?.address ?? '-' },
      { heading: 'Website', value: manufacturer?.website ?? '-' },
    ],
    [manufacturer],
  );

  const capabilities = [
    {
      heading: 'Materials',
      value: manufacturer?.technical_details?.materials?.join(', ') ?? '-',
    },
    {
      heading: 'List Of Manufacturing Processes',
      value: manufacturer?.technical_details?.manufacturing_processes?.join(
        ', ' ?? '-',
      ),
    },
    {
      heading: 'Production Capacity',
      value: manufacturer?.technical_details?.capacity ?? '-',
    },
  ];

  const plantCardData = useMemo(() => {
    if (!manufacturer?.plant_info) return [];

    return manufacturer.plant_info.map((plant, idx) => ({
      id: plant.plant_id || idx,
      name: plant.plant_name || `Plant ${idx + 1}`,

      imageUrl: 'https://placehold.co/600x400?text=Plant+Photo',
      buttonText: 'View Details',
    }));
  }, [manufacturer]);
  const auditSummary = [
    { heading: 'Audit Date', value: '--' },
    { heading: 'Number Of Major Non-Conformities', value: '--' },
    { heading: 'Number Of Minor Non-Conformities', value: '--' },
    { heading: 'Number Of Improvement Potentials', value: '--' },
    { heading: 'Number Of Questions With 0 Marks', value: '--' },
    { heading: 'Audit Score', value: '--' },
  ];

  const certificateData = [
    { id: 1, label: 'Owner ID', verified: true },
    { id: 2, label: 'QMS Certificate', verified: true },
    { id: 3, label: 'ISO Certificate', verified: true },
    { id: 4, label: 'AS9100 Certificate', verified: true },
    { id: 5, label: 'Pollution Control Certificate', verified: true },
  ];
  const verificationData = [
    { id: 1, label: 'Tax Regestration', verified: true },
    { id: 2, label: 'Excise Regestration', verified: true },
    { id: 3, label: 'PAN Number', verified: true },
    { id: 4, label: 'Factory License Number', verified: true },
  ];
  const DocumentCard = ({
    name,
    imageUrl,
    buttonText = 'View All', // fallback keeps old behaviour
  }) => (
    <div className="border border-gray-200 rounded-xl p-4 shadow-sm flex flex-col h-full">
      <div className="mb-3">
        <span className="text-gray-500 text-sm mr-2">Plant Name</span>
        <span className="font-medium">{name}</span>
      </div>

      <div className="relative w-full h-48 overflow-hidden rounded mb-4">
        <img src={imageUrl} alt={name} className="object-cover w-full h-full" />
      </div>

      <button
        type="button"
        className="mt-auto w-full py-1 border border-darkerPurple text-darkerPurple rounded-md font-medium hover:bg-gray-50 transition mt-4"
      >
        {buttonText}
      </button>
    </div>
  );

  const attributes = [
    {
      title: 'Competency',
      description:
        "Supplier’s capabilities aligned with buyer's technical and quality needs.",
      selected: true,
    },
    {
      title: 'Capacity',
      description:
        'Ability to handle buyer’s project scale and delivery timeline.',
      selected: true,
    },
    {
      title: 'Commitment',
      description: 'Adherence to quality standards and certifications.',
      selected: true,
    },
    {
      title: 'Control',
      description:
        'Manufacturing control, process integrity & supply chain efficiency.',
      selected: true,
    },
    {
      title: 'Cash',
      description: 'Financial stability and risk profile.',
      selected: true,
    },
    {
      title: 'Cost',
      description: 'Competitiveness in pricing without compromising quality.',
      selected: true,
    },
    {
      title: 'Consistency',
      description: 'Delivery track record and reliability.',
      selected: true,
    },
    {
      title: 'Culture',
      description: 'Ethical practices and governance.',
      selected: true,
    },
    {
      title: 'Clean',
      description: 'Environmental responsibility and sustainability practices.',
      selected: true,
    },
    {
      title: 'Communication',
      description: 'Responsiveness, clarity, and ability to collaborate.',
      selected: true,
    },
  ];

  return (
    <div className="font-roboto bg-backgroundGray min-h-[calc(100vh-70px)] relative">
      <div className="absolute left-20 top-3">
        <Breadcrumb
          steps={steps}
          className="bg text-sm w-full"
          currentStep={sectionId}
        />
      </div>

      <div className="grid grid-cols-customCP12 min-h-[calc(100vh-70px)]">
        <div className="lg:hidden col-span-12 p-4">
          <Drawer>
            <DrawerTrigger asChild>
              <Menu size={28} className="cursor-pointer mt-7 ml-5" />
            </DrawerTrigger>
            <DrawerContent>
              <div className="mx-auto w-full max-w-sm">
                <DrawerHeader>
                  <DrawerTitle>Menu</DrawerTitle>
                </DrawerHeader>
                <LeftSidebar role="buyer" />
                <DrawerClose asChild>
                  <Button variant="outline" className="w-full">
                    Close
                  </Button>
                </DrawerClose>
              </div>
            </DrawerContent>
          </Drawer>
        </div>

        {/* Desktop Sidebar */}
        <div className="hidden lg:block lg:col-span-1">
          <div className="side-menu h-full pt-5">
            <SidebarSheetWrapper role="buyer" />
          </div>
        </div>

        {/* Main Content */}
        {loading ? (
          <div className="col-span-11 mt-10 mb-4 mx-3 rounded-lg relative overflow-hidden">
            <div className="font-roboto grid grid-cols-12 gap-x-6 min-h-[calc(100vh-70px)] overflow-hidden">
              <div className="col-span-9 w-full min-h-[calc(100vh-70px)] bg-white rounded p-4 gap-4">
                <div className="flex flex-row items-center gap-2 text-darkPurple">
                  <Loader2 className="h-12 w-12 animate-spin " />
                  <span className="text-4xl">Loading</span>
                </div>
              </div>

              <div className="col-span-3 rounded h-full max-h-screen overflow-y-auto">
                <div className="p-4 rounded bg-white">
                  <div className="bg-[#B2BBC6] h-28 w-full rounded mb-2"></div>
                  <p className="text-sm mb-1">
                    Secure Collaboration with an NDA
                  </p>
                  <p className="text-xs text-gray-600">
                    Protect your intellectual property and ideas by sharing a
                    Non-Disclosure Agreement (NDA) with this manufacturer.
                    Upload your NDA file securely to proceed.
                  </p>
                  <button className="mt-3 w-full bg-darkPurple text-white px-6 py-1 rounded hover:bg-darkerPurple ease-in duration-200">
                    Share NDA
                  </button>
                </div>

                <div className="p-4 rounded bg-white mt-4">
                  <p className="text-sm mb-1">Get Answers Before You Decide</p>
                  <p className="text-xs text-gray-600">
                    Have specific questions about materials, processes, or
                    certifications? Reach out to the manufacturer for
                    clarification
                  </p>
                  <button className="mt-3 w-full bg-darkPurple text-white px-6 py-1 rounded hover:bg-darkerPurple ease-in duration-200">
                    Ask Questions
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="col-span-11 mt-10 mb-4 mx-3 rounded-lg relative overflow-hidden">
            <div className="font-roboto grid grid-cols-12 gap-x-6 min-h-[calc(100vh-70px)] overflow-hidden">
              {/* Main section */}
              <div className="col-span-9">
                {/* Upper Section */}
                <div className="flex flex-row items-center bg-white rounded-lg p-4 gap-4">
                  <div>
                    <img
                      src="/path/to/logo.png"
                      alt={manufacturer?.company_name}
                      className="w-20 h-20 rounded border"
                    />
                  </div>
                  <div className="h-full flex flex-col flex-grow gap-2">
                    <h2 className="font-semibold text-lg">
                      {manufacturer?.company_name}
                    </h2>
                    <div className="flex items-center text-yellow-500">
                      {[...Array(4)].map((_, i) => (
                        <Star
                          key={i}
                          size={16}
                          fill="currentColor"
                          stroke="none"
                        />
                      ))}
                      <StarHalf size={16} fill="currentColor" stroke="none" />
                    </div>
                  </div>
                  <div className="flex flex-col space-y-2 items-end">
                    <Button className="bg-white border border-darkerPurple text-darkerPurple hover:bg-gray-50 px-3 py-0">
                      ShortList
                    </Button>
                  </div>
                </div>

                {/* lower section */}
                <div className="mt-6 bg-white rounded-lg p-4">
                  {/* Supplier Information Section */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="bg-gray-100 text-darkPurple font-semibold rounded p-2">
                      Company Overview
                    </h3>
                    <div className="grid grid-rows-4 grid-flow-col gap-x-16 gap-y-8 mt-6">
                      {companyOverview.map(({ heading, value }, idx) => (
                        <div key={idx}>
                          <p className="text-gray-500 text-sm">{heading}</p>
                          <p className="text-base mt-1">{value}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 mt-8">
                    <h3 className="bg-gray-100 text-darkPurple font-semibold rounded p-2">
                      Capabilities & Expertise
                    </h3>
                    <div className="grid grid-cols-12 gap-y-6 mt-6">
                      {capabilities.map(({ heading, value }, idx) => (
                        <React.Fragment key={idx}>
                          {' '}
                          <span className="col-span-3 text-gray-500 text-sm">
                            {heading}
                          </span>
                          <span className="col-span-9 text-base">{value}</span>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 mt-8">
                    <button
                      onClick={() => setOpenPlant(!OpenPlant)}
                      className="w-full flex items-center bg-gray-100 justify-between p-2 text-darkerPurple"
                    >
                      Plant & Machines
                      <svg
                        className={`w-4 h-4 transform transition-transform duration-300 ${
                          OpenPlant ? 'rotate-180' : 'rotate-0'
                        }`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {OpenPlant && (
                      <div className="transition-all ease-in duration-300">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                          {plantCardData.map((item) => (
                            <DocumentCard key={item.id} {...item} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 mt-8 transition-all ease-in duration-300"
                  onClick={() => setOpenCertification(!OpenCertification)}>
                    <div
                      
                      className="w-full flex items-center bg-gray-100 justify-between p-2 text-darkerPurple"
                    >
                      Certifications
                    </div>
                    <div className="flex justify-between mt-4">
                      <div className="text-gray-400">
                        These certificates have been submitted by the
                        manufacturer to <span>MakerSharks</span>{' '}
                      </div>

                      <div>
                        <svg
                          className={`w-4 h-4 transform transition-transform duration-300 ${
                            OpenCertification ? 'rotate-180' : 'rotate-0'
                          }`}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                    {OpenCertification && (
                      <div className="transition-all ease-in duration-300">
                        <div className="bg-gray-50 p-6 rounded-lg">
                          <ul className="space-y-3">
                            {certificateData.map(({ id, label, verified }) => (
                              <li
                                key={id}
                                className="grid grid-cols-3 bg-white rounded-md px-6 py-4 shadow-sm"
                              >
                                {/* Certificate name */}
                                <span className="font-medium text-gray-800">
                                  {label}
                                </span>

                                {/* Verified badge */}
                                <span className="flex items-center gap-2">
                                  {verified && (
                                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-sm bg-green-600">
                                      <Check
                                        className="w-4 h-4 text-white"
                                        strokeWidth={3}
                                      />
                                    </span>
                                  )}
                                  <span className="text-sm text-gray-700">
                                    {verified ? 'Verified' : 'Pending'}
                                  </span>
                                </span>

                                {/* View button */}
                                <div className="text-right">
                                  <button
                                    type="button"
                                    className="px-1 py-1.5 text-sm border border-purple-600 text-purple-600 rounded hover:bg-purple-50 transition w-32"
                                  >
                                    View
                                  </button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>

                  <div
                    className="bg-gray-50 rounded-lg p-4 mt-8"
                    onClick={() => setOpenVerified(!OpenVerified)}
                  >
                    <div>
                      <div className="w-full flex items-center bg-gray-100 justify-between p-2 text-darkerPurple">
                        Verified By MakerSharks
                      </div>
                      <div className="flex justify-between mt-4">
                        <div className="text-gray-400">
                          This manufacturer has submitted key business and
                          compliance documents reviewd by MakerSharks to ensure
                          Authenticity, <br></br> operational credibility and
                          legal standing{' '}
                        </div>

                        <div>
                          <svg
                            className={`w-4 h-4 transform transition-transform duration-300 ${
                              OpenVerified ? 'rotate-180' : 'rotate-0'
                            }`}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                    {OpenVerified && (
                      <div className="transition-all ease-in duration-300">
                        <div className="bg-gray-50 p-6 rounded-lg">
                          <ul className="space-y-3">
                            {certificateData.map(({ id, label, verified }) => (
                              <li
                                key={id}
                                className="grid grid-cols-2 bg-white rounded-md px-6 py-4 shadow-sm"
                              >
                                {/* Certificate name */}
                                <span className="font-medium text-gray-800">
                                  {label}
                                </span>

                                {/* Verified badge */}
                                <div className="text-right">
                                  <span className="flex items-center gap-2">
                                    {verified && (
                                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-sm bg-green-600">
                                        <Check
                                          className="w-4 h-4 text-white"
                                          strokeWidth={3}
                                        />
                                      </span>
                                    )}
                                    <span className="text-sm text-gray-700">
                                      {verified ? 'Verified' : 'Pending'}
                                    </span>
                                  </span>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="bg-gray-50 rounded-lg mt-8 p-4">
                    <div className="bg-gray-100 text-darkerPurple font-semibold p-2">
                      Manufacture Evaluation
                    </div>

                    <div className="mt-6 text-gray-400">
                      A comprehensive evaluation of this supplier based on
                      global best practices using the{' '}
                      <span className="text-gray-600 font-semibold">10C</span>{' '}
                      framework
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
                      {attributes.map((attr, index) => (
                        <div
                          key={index}
                          className={`p-4 border rounded-lg shadow-sm bg-white border-gray-300 shadow-md hover:cursor-pointer hover:bg-veryVeryLightPurple transition-all ease-in duration-200`}
                        >
                          <div className="flex items-start gap-2 mb-2">
                            {attr.selected && (
                              <span className="text-green-600 text-lg font-bold">
                                ✔
                              </span>
                            )}
                            <h3 className=" text-md">{attr.title}</h3>
                          </div>
                          <p className="text-sm text-gray-400">
                            {attr.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Sidebar */}
              <div className="col-span-3 rounded h-full max-h-screen overflow-y-auto">
                <div className="p-4 rounded bg-white">
                  <div className="bg-[#B2BBC6] h-28 w-full rounded mb-2"></div>
                  <p className="text-sm mb-1">
                    Secure Collaboration with an NDA
                  </p>
                  <p className="text-xs text-gray-600">
                    Protect your intellectual property and ideas by sharing a
                    Non-Disclosure Agreement (NDA) with this manufacturer.
                    Upload your NDA file securely to proceed.
                  </p>
                  <button className="mt-3 w-full bg-darkPurple text-white px-6 py-1 rounded hover:bg-darkerPurple ease-in duration-200">
                    Share NDA
                  </button>
                </div>

                <div className="p-4 rounded bg-white mt-4">
                  <p className="text-sm mb-1">Get Answers Before You Decide</p>
                  <p className="text-xs text-gray-600">
                    Have specific questions about materials, processes, or
                    certifications? Reach out to the manufacturer for
                    clarification
                  </p>
                  <button className="mt-3 w-full bg-darkPurple text-white px-6 py-1 rounded hover:bg-darkerPurple ease-in duration-200">
                    Ask Questions
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BuyerManufacturerProfile;
